import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../lib/firebase";
import { methodologyService } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Search, CheckCircle, XCircle, ExternalLink, AlertCircle } from "lucide-react";
import { toast } from "../../components/ui/use-toast";

const ADMIN_EMAIL = "admin@rawpa.com";

interface OSINTSettings {
  enabled: boolean;
  description: string;
}

export const AdminOSINTPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settings, setSettings] = useState<OSINTSettings>({
    enabled: true,
    description: "A collection of essential OSINT tools, commands, and links for reconnaissance."
  });
  const [saving, setSaving] = useState(false);
  const [tools, setTools] = useState<any[]>([]);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [loadingTools, setLoadingTools] = useState(true);

  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      return () => {};
    }
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
        return;
      }
      loadSettings();
      loadTools();
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      const systemSettings = await methodologyService.getSystemSettings();
      setSettings({
        enabled: !systemSettings.disableOSINT,
        description: systemSettings.osintDescription || "A collection of essential OSINT tools, commands, and links for reconnaissance."
      });
    } catch (error) {
      setError("Failed to load OSINT settings");
    }
  };

  const loadTools = async () => {
    try {
      const response = await fetch('/data/osint-tools.json');
      if (!response.ok) {
        throw new Error('Failed to fetch tools data');
      }
      const allTools = await response.json();
      setTools(allTools);
      setAllCategories([...new Set(allTools.map((t: any) => t.category))]);
    } catch (error) {
      console.error('Error loading tools:', error);
      setTools([]);
    } finally {
      setLoadingTools(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await methodologyService.updateSystemSettings({
        disableOSINT: !settings.enabled,
        osintDescription: settings.description
      });
      toast({
        title: "Settings Saved",
        description: "OSINT Toolkit settings have been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Save Failed",
        description: "Failed to save OSINT settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Filter tools based on search and category
  const filteredTools = tools.filter(tool => {
    if (category && tool.category !== category) return false;
    if (q) {
      const qStr = q.toLowerCase();
      return tool.name.toLowerCase().includes(qStr) || 
             tool.description.toLowerCase().includes(qStr) || 
             tool.category.toLowerCase().includes(qStr);
    }
    return true;
  });

  function handleApproveTool(idx: number, approved: boolean) {
    const updated = [...tools];
    const toolIndex = tools.findIndex(t => t === filteredTools[idx]);
    if (toolIndex !== -1) {
      updated[toolIndex].approved = approved;
      setTools(updated);
      // Note: In a real app, this would save to a backend API
      toast({
        title: "Tool Updated",
        description: `Tool ${approved ? 'approved' : 'hidden'} successfully.`,
      });
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/admin')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/aAyuiOcvMq/toolkit')} className="font-['Space_Grotesk'] text-black">Back to Toolkits</Button>
              <div onClick={handleSignOut} className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"><img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" /></div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-indigo-500 rounded-lg flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">OSINT Toolkit Management</h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">Manage Open-Source Intelligence tools and reconnaissance techniques</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Settings Panel */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable/Disable Toggle */}
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-base font-medium">Enable OSINT Toolkit</Label>
                    <p className="text-sm text-gray-500">Allow users to access the OSINT toolkit</p>
                  </div>
                  <Switch checked={settings.enabled} onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))} />
                </div>
                {/* Description */}
                <div className="space-y-2">
                  <Label className="text-base font-medium">Description</Label>
                  <Textarea value={settings.description} onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))} placeholder="Enter description for OSINT Toolkit" className="min-h-[100px]" />
                </div>
                {/* Save Button */}
                <Button onClick={handleSaveSettings} disabled={saving} className="w-full bg-[#daed6e] hover:bg-[#c5d85c] text-black">{saving ? "Saving..." : "Save Settings"}</Button>
              </CardContent>
            </Card>

            {/* User Preview */}
            <Card className="border-2 border-[#E7E7E7]">
              <CardHeader>
                <CardTitle className="text-xl text-black">User Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-indigo-500 rounded-lg flex items-center justify-center">
                      <Search className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-['Space_Grotesk'] font-bold text-lg text-black">OSINT Toolkit</h3>
                      <p className="text-sm text-gray-600">{settings.description}</p>
                    </div>
                    <Badge variant={settings.enabled ? "default" : "secondary"}>{settings.enabled ? "Available" : "Disabled"}</Badge>
                  </div>
                  {settings.enabled && (
                    <div className="space-y-3">
                      <p className="text-sm text-gray-600">Users will see the OSINT Toolkit as an option in their toolkit page with features:</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                        <Badge variant="outline">Subdomain Discovery</Badge>
                        <Badge variant="outline">Email Harvesting</Badge>
                        <Badge variant="outline">Metadata Extraction</Badge>
                        <Badge variant="outline">Social Media</Badge>
                        <Badge variant="outline">Shodan/Censys</Badge>
                        <Badge variant="outline">Google Dorks</Badge>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12">
            <h2 className="text-xl font-bold mb-4">Curate OSINT Tools</h2>
            <div className="flex flex-wrap gap-4 mb-4">
              <input type="text" placeholder="Search tools..." value={q} onChange={e => setQ(e.target.value)} className="border rounded px-3 py-2 text-sm" />
              <select value={category} onChange={e => setCategory(e.target.value)} className="border rounded px-3 py-2 text-sm">
                <option value="">All Categories</option>
                {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
              <Button variant="outline" onClick={() => { setQ(''); setCategory(''); }}>Clear Filters</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredTools.map((tool, idx) => (
                <Card key={idx} className="mb-4 border-2 border-[#E7E7E7]">
                  <CardHeader>
                    <CardTitle className="text-xl text-black flex items-center gap-2">
                      <Badge variant="outline" className="text-[#b2c800] border-[#a0a000]">{tool.category}</Badge>
                    </CardTitle>
                    <p className="text-gray-600 mt-2">{tool.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="font-semibold text-black mb-1">{tool.name}</div>
                    <div className="flex items-center gap-2 mb-2">
                      <Input value={tool.link} readOnly className="font-mono text-xs" />
                      <Button size="sm" variant="outline" asChild><a href={tool.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-4 h-4" /></a></Button>
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      <Switch checked={tool.approved !== false} onCheckedChange={checked => handleApproveTool(idx, checked)} />
                      <span className="text-xs">{tool.approved === false ? 'Hidden' : 'Approved'}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!loading && filteredTools.length === 0 && <div className="text-gray-500 col-span-2">No tools found.</div>}
            </div>
            <div className="text-xs text-gray-500 mt-8 text-center">
              Tool data sourced from <a href="https://github.com/jivoi/awesome-osint" target="_blank" rel="noopener noreferrer" className="underline">awesome-osint</a> by jivoi (MIT License).
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 